// src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import './i18n';
import Home from './components/Home';
import Bookmark from './components/Bookmark';
import ScrollToTop from './components/ScrollToTop';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ReadingProgressProvider } from './contexts/ReadingProgressContext';
import ReadingProgress from './components/stats/ReadingProgress';
import { TranslationProvider } from './contexts/TranslationContext';
import { ArabicTextCacheProvider } from './contexts/ArabicTextCacheContext';
import DuaCategories from './components/Dua/DuaCategories';
import DuaCategory from './components/Dua/DuaCategory';
import InstallPwaPrompt from './components/InstallPwaPrompt';
import UpdateAvailablePrompt from './components/UpdateAvailablePrompt';
import AudioPlaybackBlockedPrompt from './components/AudioPlaybackBlockedPrompt';
import MediaSessionAyahFocus from './components/MediaSessionAyahFocus';
import RouteDocumentTitle from './components/RouteDocumentTitle';
import { NotFoundPage } from './components/PageStatus';
import ErrorBoundary from './components/ErrorBoundary';
import { FaSpinner } from 'react-icons/fa';
import { Analytics } from '@vercel/analytics/react';

const SurahTrPageLazy = lazy(() => import('./components/SurahTrPage'));
const ArabicReadingLazy = lazy(() => import('./components/ArabicReading'));
const DuaPageLazy = lazy(() => import('./components/Dua/Duapage'));
const ThirteenLinePageLazy = lazy(() => import('./components/pages/ThirteenLinePage'));
const AboutLazy = lazy(() => import('./components/About'));
const TopicsTestLazy = lazy(() => import('./components/Topics/TopicsTest'));
const SearchPageLazy = lazy(() => import('./components/SearchPage'));

function App() {
  // Prewarm common surahs + search index off the critical path
  useEffect(() => {
    const warm = () => {
      import('./components/WordbyWord').then(({ loadSurahChunk }) => {
        const commonSurahs = [1, 2, 18, 36, 55, 67, 112, 113, 114];
        commonSurahs.forEach((surahId) => {
          loadSurahChunk(surahId).catch(() => {});
        });
      });
      import('./utils/clientSearch').then(({ prefetchSearchIndex, resolveSearchLangs }) => {
        let langs = ['gu'];
        try {
          const stored = JSON.parse(localStorage.getItem('selectedTranslations') || 'null');
          const i18nLang = localStorage.getItem('lang') || localStorage.getItem('i18nextLng');
          langs = resolveSearchLangs(
            Array.isArray(stored) ? stored : ['gujarati'],
            i18nLang
          );
        } catch {
          /* keep default */
        }
        prefetchSearchIndex(langs);
      });
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(warm);
    } else {
      setTimeout(warm, 0);
    }
  }, []);

  return (
    <TranslationProvider>
    <Analytics />
    <ReadingProgressProvider>
      <ArabicTextCacheProvider>
        <Router>
          <div className="Quran">
            <Header />
            <RouteDocumentTitle />
            <MediaSessionAyahFocus />
            <ToastContainer position="top-right" autoClose={2000} />
            <ErrorBoundary>
              <Suspense fallback={
                <div className="flex justify-center items-center min-h-screen">
                  <FaSpinner className="w-8 h-8 animate-spin text-gray-500" />
                </div>
              }>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/surah/:surahId" element={<SurahTrPageLazy />} />
                  <Route path="/surah/:surahId/:ayahNum" element={<SurahTrPageLazy />} />
                  <Route path="/bookmarks" element={<Bookmark />} />
                  <Route path="/arabicreading/:surahId" element={<ArabicReadingLazy />} />
                  <Route path="/progress" element={<ReadingProgress />} />
                  <Route path="/duas" element={<DuaPageLazy/>} />
                  <Route path="/duacategories" element={<DuaCategories />} />
                  <Route path="/duacategories/:category" element={<DuaCategory />} />
                  <Route path="/13linepage" element={<ThirteenLinePageLazy />} />
                  <Route path="/topics-test" element={<TopicsTestLazy />} />
                  <Route path="/about" element={<AboutLazy />} />
                  <Route path="/search" element={<SearchPageLazy />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                <ScrollToTop />
                <InstallPwaPrompt />
                <UpdateAvailablePrompt />
                <AudioPlaybackBlockedPrompt />
              </Suspense>
            </ErrorBoundary>
          </div>
        </Router>
      </ArabicTextCacheProvider>
    </ReadingProgressProvider>
    </TranslationProvider>
  );
}

export default App;
